import { createSelector } from 'reselect';
import { KeywordVisibilityFilters } from './reducers/keywordReducer';
import { escapeRegExp } from '../utils/stringUtils';

const selectKeywordsState = state => state.keywords;

export const selectAllKeywords = createSelector([selectKeywordsState], keywordsState => keywordsState.allKeywords);
export const selectFilterChips = createSelector([selectKeywordsState], keywordsState => keywordsState.filterChips);
export const selectKeywordVisibilityFilter = createSelector([selectKeywordsState], keywordsState => keywordsState.keywordVisibilityFilter);
export const selectGlobalRegexError = createSelector([selectKeywordsState], keywordsState => keywordsState.regexError); // Renamed
export const selectSelectedKeywordIds = createSelector([selectKeywordsState], keywordsState => keywordsState.selectedKeywordIds);
export const selectLastSelectedKeywordId = createSelector([selectKeywordsState], keywordsState => keywordsState.lastSelectedKeywordId);
export const selectSuperSelectMode = createSelector([selectKeywordsState], keywordsState => keywordsState.superSelectMode);
export const selectColumnDefinitions = createSelector([selectKeywordsState], keywordsState => keywordsState.columnDefinitions || []);
export const selectNGramMatchMode = createSelector([selectKeywordsState], keywordsState => keywordsState.nGramMatchMode);
export const selectImportedKeywordsRaw = createSelector([selectKeywordsState], keywordsState => keywordsState.importedKeywordsRaw);

// Specific UI state selectors for column filters
export const selectVolumeFilterUI = createSelector([selectKeywordsState], keywordsState => keywordsState.volumeFilter);
export const selectKdFilterUI = createSelector([selectKeywordsState], keywordsState => keywordsState.kdFilter);
export const selectCpcFilterUI = createSelector([selectKeywordsState], keywordsState => keywordsState.cpcFilter);


const parseChipValueToNumber = (valueString) => { // Renamed for clarity
  if (valueString === null || valueString === undefined || String(valueString).trim() === '') return null;
  const num = parseFloat(String(valueString).replace(/,/g, ''));
  return isNaN(num) ? null : num;
};

export const selectDisplayedKeywords = createSelector(
  [
    selectAllKeywords,
    selectFilterChips,
    selectKeywordVisibilityFilter,
    selectGlobalRegexError
  ],
  (allKeywords, filterChips, keywordVisibilityFilter, regexError) => {
    if (!allKeywords) return [];

    let processedKeywords = allKeywords.filter(keyword => !keyword.isDeleted);

    if (keywordVisibilityFilter === KeywordVisibilityFilters.SHOW_ACTIVE) {
      processedKeywords = processedKeywords.filter(keyword => keyword.isActive && !keyword.isCategorized);
    } else if (keywordVisibilityFilter === KeywordVisibilityFilters.SHOW_INACTIVE) {
      processedKeywords = processedKeywords.filter(keyword => !keyword.isActive && !keyword.isCategorized);
    } else { // SHOW_ALL_UNCATEGORIZED or default
      processedKeywords = processedKeywords.filter(keyword => !keyword.isCategorized);
    }

    if (filterChips && filterChips.length > 0) {
      processedKeywords = filterChips.reduce((currentKeywords, chip) => {
        if (!chip || !chip.type || !chip.params) return currentKeywords;

        switch (chip.type) {
          case 'text':
            const searchTermLower = chip.params.searchTerm.toLowerCase();
            return currentKeywords.filter(kw =>
              kw.normalizedText.toLowerCase().includes(searchTermLower)
            );
          case 'regex':
            if (regexError || !chip.params.regexString) return currentKeywords;
            try {
              const regex = new RegExp(chip.params.regexString, 'i');
              return currentKeywords.filter(kw => regex.test(kw.originalText));
            } catch (e) {
              console.error("Invalid regex in chip during selection:", chip.params.regexString, e.message);
              return currentKeywords;
            }
          case 'column':
            const { columnName, min: minStr, max: maxStr } = chip.params; // columnName here is col_X

            if (!columnName) return currentKeywords; // Should not happen if chip is well-formed

            return currentKeywords.filter(kw => {
              const kwValStr = kw.data[columnName];
              const kwVal = parseChipValueToNumber(kwValStr); // Use the renamed helper

              if (kwVal === null) return false; // Keyword without this data or unparseable data fails filter

              const minVal = parseChipValueToNumber(minStr);
              const maxVal = parseChipValueToNumber(maxStr);

              let passesMin = true;
              if (minVal !== null) {
                passesMin = kwVal >= minVal;
              }

              let passesMax = true;
              if (maxVal !== null) {
                passesMax = kwVal <= maxVal;
              }

              return passesMin && passesMax;
            });
          case 'ngram':
            const { nGramText, matchMode } = chip.params;
            const lowerNGramText = nGramText.toLowerCase();
            if (matchMode === 'wholeWord') {
              const escNGramText = escapeRegExp(nGramText);
              const regex = new RegExp(`\\b${escNGramText}\\b`, 'i');
              return currentKeywords.filter(kw => regex.test(kw.normalizedText));
            } else {
              return currentKeywords.filter(kw => kw.normalizedText.toLowerCase().includes(lowerNGramText));
            }
          default:
            return currentKeywords;
        }
      }, processedKeywords);
    }
    return processedKeywords;
  }
);
